import { Fragment, type ReactNode } from 'react';
import classes from './MessageText.module.scss';

interface MessageTextProps {
    text: string;
}

interface LinkCandidate {
    text: string;
    trailingText: string;
}

const URL_PATTERN = /\b(?:https?:\/\/|www\.|(?:t|telegram)\.me\/|tg:\/\/)[^\s<>"']+/giu;
const TRAILING_PUNCTUATION = new Set(['.', ',', '!', '?', ';', ':']);

const CLOSING_PAIRS = {
    ')': '(',
    ']': '[',
    '}': '{',
} as const;

/** Подсчитывает вхождения символа в адресе */
const countCharacter = (value: string, character: string): number => (
    [...value].filter((item) => item === character).length
);

/** Определяет лишнюю закрывающую скобку в адресе */
const hasUnmatchedClosingCharacter = (
    value: string,
    closingCharacter: keyof typeof CLOSING_PAIRS,
): boolean => (
    countCharacter(value, closingCharacter)
    > countCharacter(value, CLOSING_PAIRS[closingCharacter])
);

/** Отделяет знаки после ссылки, сохраняя скобки внутри адреса */
const splitTrailingText = (candidate: string): LinkCandidate => {
    let text = candidate;
    let trailingText = '';

    while (text.length > 0) {
        const lastCharacter = text[text.length - 1];

        const isTrailingPunctuation = lastCharacter
            ? TRAILING_PUNCTUATION.has(lastCharacter)
            : false;

        const isUnmatchedClosingCharacter = lastCharacter
            ? lastCharacter in CLOSING_PAIRS
            && hasUnmatchedClosingCharacter(
                text,
                lastCharacter as keyof typeof CLOSING_PAIRS,
            )
            : false;

        if (!isTrailingPunctuation && !isUnmatchedClosingCharacter) break;

        trailingText = `${lastCharacter}${trailingText}`;
        text = text.slice(0, -1);
    }

    return {
        text,
        trailingText
    };
};

/** Возвращает адрес веб- или Telegram-ссылки с допустимым протоколом */
const getSafeHref = (value: string): string | null => {
    const normalizedValue = value.toLowerCase();

    const requiresHttps = normalizedValue.startsWith('www.')
        || normalizedValue.startsWith('t.me/')
        || normalizedValue.startsWith('telegram.me/');

    const href = requiresHttps
        ? `https://${value}`
        : value;

    try {
        const url = new URL(href);

        const isSafeProtocol = url.protocol === 'http:'
            || url.protocol === 'https:'
            || url.protocol === 'tg:';

        return isSafeProtocol
            ? href
            : null;
    } catch {
        return null;
    }
};

/** Разбивает текст на обычные фрагменты и ссылки */
const renderTextWithLinks = (text: string): ReactNode[] => {
    const nodes: ReactNode[] = [];
    let lastIndex = 0;

    for (const match of text.matchAll(URL_PATTERN)) {
        const matchIndex = match.index;
        const candidate = match[0];
        const { text: linkText, trailingText } = splitTrailingText(candidate);
        const href = getSafeHref(linkText);

        if (matchIndex > lastIndex) {
            nodes.push(text.slice(lastIndex, matchIndex));
        }

        nodes.push(href
            ? (
                <a
                    key={`link-${matchIndex}`}
                    className={classes.link}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {linkText}
                </a>
            )
            : (
                <Fragment key={`text-${matchIndex}`}>{linkText}</Fragment>
            ));

        if (trailingText) nodes.push(trailingText);
        lastIndex = matchIndex + candidate.length;
    }

    if (lastIndex < text.length) nodes.push(text.slice(lastIndex));

    return nodes;
};

/** Показывает текст сообщения и превращает безопасные веб-адреса в ссылки */
export const MessageText = ({ text }: MessageTextProps) => (
    <>{renderTextWithLinks(text)}</>
);
